import {
  RaidPlanActor,
  RaidPlanAssignmentEntity,
} from 'app/frontend/types/custom/RaidPlan'

type AggregateOverrideEntity = Extract<
  RaidPlanAssignmentEntity,
  { kind: 'everyone' | 'collection' | 'group' }
>

export type EncounterAssignmentEntityOverride =
  | {
      kind: 'participant'
      characterId: number
    }
  | {
      kind: 'aggregate'
      entity: AggregateOverrideEntity
    }

export type EncounterAssignmentAbilityOverride = {
  hidden?: boolean
  casts?: Record<
    string,
    {
      boxes?: Record<
        string,
        {
          entities?: Record<string, EncounterAssignmentEntityOverride>
        }
      >
    }
  >
}

export type EventPlanEncounterAssignmentOverrides = {
  abilities?: Record<string, EncounterAssignmentAbilityOverride>
}

export type EventPlanActorOverrides = {
  actors?: Record<
    string,
    {
      characterId?: number
      replacementActor?: RaidPlanActor
    }
  >
}

export type EventPlanOverridesByPlanId = Record<
  string,
  {
    encounterAssignments?: EventPlanEncounterAssignmentOverrides
    actorOverrides?: EventPlanActorOverrides
  }
>

type SetEncounterAssignmentEntityOverrideInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  spellId: number
  castIds: string[]
  boxId: string
  entityId: string
  override: EncounterAssignmentEntityOverride
}

type GetEncounterAssignmentEntityOverrideCharacterIdInput = {
  encounterAssignments?: EventPlanEncounterAssignmentOverrides
  spellId: number
  castIds: string[]
  boxId?: string
  entityId?: string
}

type GetEncounterAssignmentEntityOverrideInput = {
  encounterAssignments?: EventPlanEncounterAssignmentOverrides
  spellId: number
  castIds: string[]
  boxId?: string
  entityId?: string
}

type SetEncounterAssignmentAbilityHiddenInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  spellId: number
  hidden: boolean
}

type SetActorOverrideCharacterIdInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  actorUuid: string
  characterId: number
}

type SetActorOverrideReplacementActorInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  actorUuid: string
  replacementActor: RaidPlanActor
}

type GetActorOverrideCharacterIdInput = {
  actorOverrides?: EventPlanActorOverrides
  actorUuid: string
}

type GetActorOverrideReplacementActorInput = {
  actorOverrides?: EventPlanActorOverrides
  actorUuid: string
}

type ClearActorOverrideReplacementActorInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  actorUuid: string
}

type ClearActorOverridesInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
}

type ClearEncounterAssignmentAbilityOverridesInput = {
  planOverrides: EventPlanOverridesByPlanId
  planId: number
  spellId: number
}

type GetEncounterAssignmentAbilityHiddenInput = {
  encounterAssignments?: EventPlanEncounterAssignmentOverrides
  spellId: number
}

export const getPlanEncounterAssignmentOverrides = (
  planOverrides: EventPlanOverridesByPlanId | undefined,
  planId: number | undefined,
) => {
  if (!planId) return undefined
  return planOverrides?.[planId.toString()]?.encounterAssignments
}

export const getPlanActorOverrides = (
  planOverrides: EventPlanOverridesByPlanId | undefined,
  planId: number | undefined,
) => {
  if (!planId) return undefined
  return planOverrides?.[planId.toString()]?.actorOverrides
}

export const getActorOverrideCharacterId = ({
  actorOverrides,
  actorUuid,
}: GetActorOverrideCharacterIdInput) => {
  return actorOverrides?.actors?.[actorUuid]?.characterId
}

export const getActorOverrideReplacementActor = ({
  actorOverrides,
  actorUuid,
}: GetActorOverrideReplacementActorInput) => {
  return actorOverrides?.actors?.[actorUuid]?.replacementActor
}

export const setActorOverrideCharacterId = ({
  planOverrides,
  planId,
  actorUuid,
  characterId,
}: SetActorOverrideCharacterIdInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingActorOverrides = existingPlan.actorOverrides ?? {}
  const existingActors = existingActorOverrides.actors ?? {}

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: {
        ...existingActorOverrides,
        actors: {
          ...existingActors,
          [actorUuid]: {
            ...existingActors[actorUuid],
            characterId,
          },
        },
      },
    },
  }
}

export const setActorOverrideReplacementActor = ({
  planOverrides,
  planId,
  actorUuid,
  replacementActor,
}: SetActorOverrideReplacementActorInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingActorOverrides = existingPlan.actorOverrides ?? {}
  const existingActors = existingActorOverrides.actors ?? {}

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: {
        ...existingActorOverrides,
        actors: {
          ...existingActors,
          [actorUuid]: {
            ...existingActors[actorUuid],
            replacementActor,
          },
        },
      },
    },
  }
}

export const clearActorOverrideReplacementActor = ({
  planOverrides,
  planId,
  actorUuid,
}: ClearActorOverrideReplacementActorInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey]
  if (!existingPlan?.actorOverrides?.actors?.[actorUuid]) return planOverrides
  const existingActorOverrides = existingPlan.actorOverrides
  const existingActors = existingActorOverrides.actors ?? {}
  const existingActor = existingActors[actorUuid]
  if (!existingActor?.replacementActor) return planOverrides
  const nextActor = {
    ...existingActor,
    replacementActor: undefined,
  }
  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: {
        ...existingActorOverrides,
        actors: {
          ...existingActors,
          [actorUuid]: nextActor,
        },
      },
    },
  }
}

export const clearActorOverrides = ({
  planOverrides,
  planId,
}: ClearActorOverridesInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const existingPlan = planOverrides[planKey]
  if (!existingPlan?.actorOverrides) return planOverrides

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      actorOverrides: undefined,
    },
  }
}

export const setEncounterAssignmentEntityOverride = ({
  planOverrides,
  planId,
  spellId,
  castIds,
  boxId,
  entityId,
  override,
}: SetEncounterAssignmentEntityOverrideInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const spellKey = spellId.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingEncounterAssignments = existingPlan.encounterAssignments ?? {}
  const existingAbilities = existingEncounterAssignments.abilities ?? {}
  const existingAbility = existingAbilities[spellKey] ?? {}
  const existingCasts = existingAbility.casts ?? {}

  const nextCasts = castIds.reduce<typeof existingCasts>(
    (acc, castId) => {
      const existingCast = existingCasts[castId] ?? {}
      const existingBoxes = existingCast.boxes ?? {}
      const existingBox = existingBoxes[boxId] ?? {}
      const existingEntities = existingBox.entities ?? {}
      acc[castId] = {
        ...existingCast,
        boxes: {
          ...existingBoxes,
          [boxId]: {
            ...existingBox,
            entities: {
              ...existingEntities,
              [entityId]: override,
            },
          },
        },
      }
      return acc
    },
    { ...existingCasts },
  )

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      encounterAssignments: {
        ...existingEncounterAssignments,
        abilities: {
          ...existingAbilities,
          [spellKey]: {
            ...existingAbility,
            casts: nextCasts,
          },
        },
      },
    },
  }
}

export const getEncounterAssignmentEntityOverrideCharacterId = ({
  encounterAssignments,
  spellId,
  castIds,
  boxId,
  entityId,
}: GetEncounterAssignmentEntityOverrideCharacterIdInput) => {
  const override = getEncounterAssignmentEntityOverride({
    encounterAssignments,
    spellId,
    castIds,
    boxId,
    entityId,
  })
  if (!override || override.kind !== 'participant') return undefined
  return override.characterId
}

export const getEncounterAssignmentEntityOverride = ({
  encounterAssignments,
  spellId,
  castIds,
  boxId,
  entityId,
}: GetEncounterAssignmentEntityOverrideInput) => {
  if (!boxId || !entityId) return undefined
  const ability = encounterAssignments?.abilities?.[spellId.toString()]
  if (!ability?.casts) return undefined
  for (const castId of castIds) {
    const override = ability.casts[castId]?.boxes?.[boxId]?.entities?.[entityId]
    if (override) return override
  }
  return undefined
}

export const setEncounterAssignmentAbilityHidden = ({
  planOverrides,
  planId,
  spellId,
  hidden,
}: SetEncounterAssignmentAbilityHiddenInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const spellKey = spellId.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingEncounterAssignments = existingPlan.encounterAssignments ?? {}
  const existingAbilities = existingEncounterAssignments.abilities ?? {}
  const existingAbility = existingAbilities[spellKey] ?? {}

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      encounterAssignments: {
        ...existingEncounterAssignments,
        abilities: {
          ...existingAbilities,
          [spellKey]: {
            ...existingAbility,
            hidden,
          },
        },
      },
    },
  }
}

export const clearEncounterAssignmentAbilityOverrides = ({
  planOverrides,
  planId,
  spellId,
}: ClearEncounterAssignmentAbilityOverridesInput): EventPlanOverridesByPlanId => {
  const planKey = planId.toString()
  const spellKey = spellId.toString()
  const existingPlan = planOverrides[planKey] ?? {}
  const existingEncounterAssignments = existingPlan.encounterAssignments ?? {}
  const existingAbilities = existingEncounterAssignments.abilities ?? {}

  if (!existingAbilities[spellKey]) return planOverrides

  const nextAbilities = { ...existingAbilities }
  delete nextAbilities[spellKey]

  return {
    ...planOverrides,
    [planKey]: {
      ...existingPlan,
      encounterAssignments: {
        ...existingEncounterAssignments,
        abilities: nextAbilities,
      },
    },
  }
}

export const getEncounterAssignmentAbilityHidden = ({
  encounterAssignments,
  spellId,
}: GetEncounterAssignmentAbilityHiddenInput) =>
  encounterAssignments?.abilities?.[spellId.toString()]?.hidden === true
